import { useEffect, useMemo, useState } from "react";
import { Copy, ExternalLink, Landmark, RefreshCw, Shield } from "lucide-react";
import { toast } from "sonner";
import {
  DEFAULT_SELECTED_FORMSG_FIELDS,
  fetchEventGovIntegration,
  PLUMBER_FIELD_MAPPING,
  plumberIngestExample,
  plumberMappingGuideText,
  saveEventGovIntegration,
} from "@/lib/gov-integration-api";
import GovUpgradePrompt from "@/components/GovUpgradePrompt";
import { getAuthSession } from "@/lib/auth-session";

export default function GovernmentIntegrationPanel({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [entitled, setEntitled] = useState(false);
  const [plan, setPlan] = useState("studio");
  const [ingestSecret, setIngestSecret] = useState("");
  const [selectedFields, setSelectedFields] = useState(() => [...DEFAULT_SELECTED_FORMSG_FIELDS]);
  const authEmail = getAuthSession()?.email || "";

  const toggleField = (formsg, required) => {
    if (required) return;
    setSelectedFields((prev) => {
      const set = new Set(prev);
      if (set.has(formsg)) set.delete(formsg);
      else set.add(formsg);
      // Keep required fields always on
      for (const row of PLUMBER_FIELD_MAPPING) {
        if (row.required) set.add(row.formsg);
      }
      return PLUMBER_FIELD_MAPPING.map((r) => r.formsg).filter((f) => set.has(f));
    });
  };

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await fetchEventGovIntegration(eventId);
      setConfig(data.config);
      setEntitled(data.entitled === true);
      setPlan(data.plan || "studio");
    } catch (err) {
      toast.error(err?.message || "Could not load government integration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const save = async (rotate = false) => {
    if (!config) return;
    setSaving(true);
    try {
      const body = await saveEventGovIntegration(eventId, {
        registrationBackend: config.registrationBackend,
        formsg: config.formsg || {},
        plumber: config.plumber || {},
        rotateIngestSecret: rotate,
      });
      setConfig(body.config || config);
      if (body.ingestSecret) setIngestSecret(body.ingestSecret);
      toast.success(rotate ? "Ingest secret rotated" : "Government integration saved — live for production");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const activeSecret = ingestSecret || (config?.plumber?.ingestSecret !== "***" ? config?.plumber?.ingestSecret : "");

  const plumberHeaders = useMemo(() => {
    if (!activeSecret) return "";
    return [
      "Content-Type: application/json",
      `X-Aivory-Ingest-Secret: ${activeSecret}`,
      `# Or use: Authorization: Bearer ${activeSecret}`,
    ].join("\n");
  }, [activeSecret]);

  const ingestUrlEvent = config?.plumber?.ingestUrlEvent || config?.plumber?.ingestUrl || "";
  const formId = config?.formsg?.formId || "";
  const example = useMemo(
    () =>
      plumberIngestExample(eventId, {
        formId,
        ingestSecret: activeSecret || "",
        submissionId: "{{FSGID}}",
        ticketTier: "{{AttendanceType}}",
        selectedFields,
      }),
    [eventId, formId, activeSecret, selectedFields],
  );
  const exampleJson = useMemo(() => JSON.stringify(example, null, 2), [example]);
  const mappingGuide = useMemo(
    () =>
      plumberMappingGuideText({
        eventId,
        formId,
        ingestSecret: activeSecret || "",
        selectedFields,
      }),
    [eventId, formId, activeSecret, selectedFields],
  );

  if (loading) {
    return <p className="text-sm text-[var(--text-tertiary)]">Loading government integration…</p>;
  }

  if (!entitled) {
    return <GovUpgradePrompt plan={plan} defaultEmail={authEmail} compact />;
  }

  const isGov = config?.registrationBackend === "formsg";

  return (
    <div className="space-y-6" data-testid="gov-integration-panel">
      <div className="flex items-start gap-3">
        <Landmark size={20} className="text-[var(--primary)] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-heading text-lg font-medium">Singapore Government (FormSG + Plumber)</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl leading-relaxed">
            Enterprise capability. Registration PII stays in FormSG and agency M365; AivoryEMS stores only check-in refs and badge tokens.
          </p>
        </div>
      </div>

      <div className="surface p-4 border border-[var(--border-default)] space-y-4">
        <label className="text-overline block">Registration backend</label>
        <select
          className="input-dark w-full max-w-md"
          value={config?.registrationBackend || "native"}
          onChange={(e) => setConfig((c) => ({ ...c, registrationBackend: e.target.value }))}
        >
          <option value="native">AivoryEMS native form</option>
          <option value="formsg">FormSG (government data plane)</option>
        </select>

        {isGov ? (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-overline block mb-2">FormSG URL</label>
                <input
                  className="input-dark w-full"
                  placeholder="https://form.gov.sg/654ab…"
                  value={config?.formsg?.formUrl || ""}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      formsg: { ...(c.formsg || {}), formUrl: e.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <label className="text-overline block mb-2">Form ID (auto from URL)</label>
                <input
                  className="input-dark w-full font-mono text-xs"
                  placeholder="Filled on save"
                  value={config?.formsg?.formId || ""}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      formsg: { ...(c.formsg || {}), formId: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-overline block mb-2">Agency roster URL (SharePoint / Excel)</label>
                <input
                  className="input-dark w-full"
                  placeholder="https://…sharepoint.com/…"
                  value={config?.formsg?.rosterUrl || ""}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      formsg: { ...(c.formsg || {}), rosterUrl: e.target.value },
                    }))
                  }
                />
              </div>
            </div>

            <div className="rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-4 text-xs space-y-4">
              <div className="flex items-center gap-2 text-[var(--primary)] font-mono uppercase tracking-widest">
                <Shield size={12} /> Plumber Custom API (live)
              </div>

              <ol className="list-decimal list-inside text-[var(--text-secondary)] space-y-1.5 leading-relaxed">
                <li>FormSG trigger → M365 Excel / SharePoint (full roster)</li>
                <li>Custom API POST to the live URL — paste the JSON <strong>Data</strong> body below</li>
                <li>
                  Map FormSG <span className="font-mono">FSGID</span> →{" "}
                  <span className="font-mono">formsg.submissionId</span>, and{" "}
                  <span className="font-mono">AttendanceType</span> →{" "}
                  <span className="font-mono">ticketTier</span>
                </li>
                <li>
                  Auth via <span className="font-mono">ingestSecret</span> in the JSON (or connection header{" "}
                  <span className="font-mono">X-Aivory-Ingest-Secret</span>)
                </li>
              </ol>

              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-overline">FormSG → Aivory field mapping</div>
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() => copyText(mappingGuide, "Mapping guide")}
                  >
                    <Copy size={12} /> Copy mapping guide
                  </button>
                </div>
                <p className="text-[var(--text-secondary)] mb-2 leading-relaxed">
                  Tick the fields to include. The <strong>JSON body below</strong> updates live — then copy into Plumber → Data.
                </p>
                <div className="overflow-x-auto rounded border border-[var(--border-default)] bg-[var(--bg-base)]">
                  <table className="w-full text-left text-[11px]" data-testid="plumber-field-mapping">
                    <thead>
                      <tr className="border-b border-[var(--border-default)] text-[var(--text-tertiary)]">
                        <th className="px-3 py-2 font-medium w-10">Include</th>
                        <th className="px-3 py-2 font-medium">FormSG field</th>
                        <th className="px-3 py-2 font-medium">Aivory JSON path</th>
                        <th className="px-3 py-2 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PLUMBER_FIELD_MAPPING.map((row) => {
                        const checked = selectedFields.includes(row.formsg) || row.required;
                        return (
                          <tr key={row.formsg} className="border-b border-[var(--border-default)]/60 last:border-0">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                className="accent-[var(--primary)]"
                                checked={checked}
                                disabled={row.required}
                                aria-label={`Include ${row.formsg}`}
                                data-testid={`plumber-field-${row.formsg}`}
                                onChange={() => toggleField(row.formsg, row.required)}
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-[var(--text-primary)]">
                              {row.formsg}
                              {row.required ? (
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">Required</span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 font-mono text-[var(--text-primary)]">{row.aivory}</td>
                            <td className="px-3 py-2 text-[var(--text-secondary)]">{row.note}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-[var(--text-tertiary)] leading-relaxed">
                  Required: <span className="font-mono">FSGID</span> →{" "}
                  <span className="font-mono">formsg.submissionId</span>,{" "}
                  <span className="font-mono">AttendanceType</span> →{" "}
                  <span className="font-mono">ticketTier</span>. Optional fields appear in the JSON only when checked.
                  Anything else → <span className="font-mono">422</span>.
                </p>
              </div>

              <div>
                <div className="text-overline mb-1">POST URL (event in path)</div>
                <div className="font-mono break-all text-[var(--text-primary)]">{ingestUrlEvent}</div>
                {ingestUrlEvent ? (
                  <button type="button" className="btn-secondary text-xs mt-2" onClick={() => copyText(ingestUrlEvent, "Ingest URL")}>
                    <Copy size={12} /> Copy URL
                  </button>
                ) : null}
              </div>

              {activeSecret ? (
                <div>
                  <div className="text-overline mb-1">Auth (pick one)</div>
                  <p className="text-[var(--text-secondary)] mb-2 leading-relaxed">
                    Prefer <span className="font-mono">ingestSecret</span> inside the JSON Data body (see below).
                    Or set connection Headers:
                  </p>
                  <pre className="font-mono text-[10px] whitespace-pre-wrap bg-[var(--bg-base)] p-2 rounded border border-[var(--border-default)]">{plumberHeaders}</pre>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button type="button" className="btn-secondary text-xs" onClick={() => copyText(plumberHeaders, "Plumber headers")}>
                      <Copy size={12} /> Copy headers
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      onClick={() => copyText(activeSecret, "Ingest secret")}
                    >
                      <Copy size={12} /> Copy secret only
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-amber-400">Click <strong>Save & go live</strong> to generate your ingest secret.</p>
              )}

              <div>
                <div className="text-overline mb-1">Plumber → Data (JSON)</div>
                <p className="text-[var(--text-secondary)] mb-2 leading-relaxed">
                  Built from your field selection above. Paste into Custom API → <strong>Data</strong>.
                  FormSG variables use <span className="font-mono">{"{{FieldName}}"}</span> syntax.
                </p>
                <pre
                  className="font-mono text-[10px] whitespace-pre-wrap bg-[var(--bg-base)] p-2 rounded border border-[var(--border-default)] max-h-56 overflow-auto"
                  data-testid="plumber-json-body"
                >
                  {exampleJson}
                </pre>
                <button
                  type="button"
                  className="btn-secondary text-xs mt-2"
                  onClick={() => copyText(exampleJson, "Plumber JSON body")}
                >
                  <Copy size={12} /> Copy JSON body
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-[var(--text-tertiary)]">
              <span>Sync: {config?.syncStatus || "—"}</span>
              {config?.lastIngestAt ? <span>· Last ingest {config.lastIngestAt}</span> : null}
              {config?.ingestCount ? <span>· {config.ingestCount} live registrations</span> : null}
            </div>
          </>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary" disabled={saving} onClick={() => save(false)}>
            {saving ? "Saving…" : isGov ? "Save & go live" : "Save"}
          </button>
          {isGov ? (
            <button type="button" className="btn-secondary" disabled={saving} onClick={() => save(true)}>
              <RefreshCw size={14} /> Rotate ingest secret
            </button>
          ) : null}
          {config?.formsg?.formUrl ? (
            <a href={config.formsg.formUrl} target="_blank" rel="noreferrer" className="btn-secondary">
              <ExternalLink size={14} /> Open FormSG
            </a>
          ) : null}
          {config?.formsg?.rosterUrl ? (
            <a href={config.formsg.rosterUrl} target="_blank" rel="noreferrer" className="btn-secondary">
              <ExternalLink size={14} /> Agency roster
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
